import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import useToast from '@/hooks/use-toast'
import { ref } from 'vue'
import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { getPersistedRegion, deletePersistedRegion } from '@/services/region.service'
import { logger } from '@/utils/logger'

const { queueToast } = useToast()
export const LOCAL_HOST = 'http://localhost:3001/v1'

export const STAGING_API = 'https://staging-api.agentnoon.co/v1'

export const PRODUCTION_API = 'https://api.agentnoon.co/v1'
export const AUS_PRODUCTION_API = 'https://api-aus.agentnoon.co/v1'
export const EU_PRODUCTION_API_V2 = 'https://api-eu-v2.agentnoon.co/v1'
export const SINGAPORE_PRODUCTION_API = 'https://api-singapore.agentnoon.co/v1'
export const AJZ_PRODUCTION_API = 'https://hcm.aljazeera.net/v1'
export const UAE_PRODUCTION_API = 'https://api-uae.agentnoon.co/v1'
export const INTERNAL_PRODUCTION_API = 'https://api-internal.agentnoon.co/v1'
export const DAYFORCE_PROTOTYPE_API = 'https://dayforce-prototype.agentnoon.co/v1'
export const DAYFORCE_PROTOTYPE_2_API = 'https://dayforce-prototype-2.agentnoon.co/v1'

// Dayforce API base URLs (fixed per environment; NP and PRE have one backend each)
export const DAYFORCE_NP_API = 'https://agentnoon-agentnoon-ue2-dev.np.dayforcehcm.com/v1'
export const DAYFORCE_PRE_API = 'https://agentnoon-agentnoon-ue2-test.pre.dayforce.com/v1'
export const DAYFORCE_PROD_API = 'https://agentnoon-agentnoon-ue2.dayforce.com/v1'
export const DAYFORCE_AU_API = 'https://agentnoon-agentnoon-aus.dayforce.com/v1'
export const DAYFORCE_CA_API = 'https://agentnoon-agentnoon-cac.dayforce.com/v1'
export const DAYFORCE_EU_API = 'https://agentnoon-agentnoon-euw.dayforce.com/v1'

// Export all API URLs for Faro configuration
export const ALL_API_URLS = [
  STAGING_API,
  PRODUCTION_API,
  AUS_PRODUCTION_API,
  EU_PRODUCTION_API_V2,
  SINGAPORE_PRODUCTION_API,
  AJZ_PRODUCTION_API,
  UAE_PRODUCTION_API,
  INTERNAL_PRODUCTION_API,
  DAYFORCE_PROTOTYPE_API,
  DAYFORCE_PROTOTYPE_2_API
]

// Add a flag to track if firewall warning toast is showing
const isFirewallToastShowing = ref(false)

// Return a list of endpoints to fetch from based on environment
// - prod: production regional APIs only
// - staging/preview: staging and localhost
export const getAllowedEndpoints = async () => {
  const host = window.location.hostname

  if (host.includes('dayforcehcm.com') || host.includes('dayforce.com')) {
    if (host.includes('np.dayforce') || host.includes('np.mydayforce')) {
      return new Map([['eastus2', DAYFORCE_NP_API]])
    }
    if (host.includes('pre.dayforce') || host.includes('pre.mydayforce')) {
      return new Map([['eastus2', DAYFORCE_PRE_API]])
    }
    return new Map([
      ['eastus2', DAYFORCE_PROD_API],
      ['australiaeast', DAYFORCE_AU_API],
      ['canadacentral', DAYFORCE_CA_API],
      ['westeurope', DAYFORCE_EU_API]
    ])
  }

  // Preview sites are treated like staging
  const isPreview = host.includes('.web.app')
  const isStaging = host.includes('staging') || isPreview
  const isLocal = host.includes('localhost')
  const isProductionMode = import.meta.env.MODE === 'production'
  const endpoints = new Map()
  if ((isStaging || isLocal) && !isProductionMode) {
    if (isPreview) {
      endpoints.set('preview', getPreviewUrl())
      endpoints.set('staging', STAGING_API)
    } else if (isStaging && STAGING_API) {
      endpoints.set('staging', STAGING_API)
    } else {
      if (LOCAL_HOST) endpoints.set('local', LOCAL_HOST)
      if (STAGING_API) endpoints.set('staging', STAGING_API)
    }
    return endpoints
  }

  // Production: include public regional Agentnoon APIs (exclude self-hosted AJZ)
  endpoints.set('dayforce-prototype', DAYFORCE_PROTOTYPE_API)
  endpoints.set('dayforce-prototype-2', DAYFORCE_PROTOTYPE_2_API)
  endpoints.set('us', PRODUCTION_API)
  endpoints.set('aus', AUS_PRODUCTION_API)
  endpoints.set('eu', EU_PRODUCTION_API_V2)
  endpoints.set('singapore', SINGAPORE_PRODUCTION_API)
  endpoints.set('uae', UAE_PRODUCTION_API)
  endpoints.set('internal', INTERNAL_PRODUCTION_API)
  endpoints.set('localhost', LOCAL_HOST)
  return endpoints
}

// Fetch boards from all allowed endpoints. Returns Map<regionKey, boards[]>
export const fetchBoardsAcrossRegions = async () => {
  const endpoints = await getAllowedEndpoints()
  const headers = { ...(await getAuthHeader(true)) }

  const entries = await Promise.all(
    Array.from(endpoints.entries()).map(async ([regionKey, baseUrl]) => {
      try {
        const res = await axios.get(`${baseUrl}/boards?masterOnly=true`, { headers })
        const boards = Array.isArray(res.data) ? res.data : []
        return [regionKey, boards.filter((b) => b.masterBranch)]
      } catch (e) {
        logger.warn('Failed fetching boards for region:', regionKey, e?.message)
        return [regionKey, []]
      }
    })
  )

  return new Map(entries)
}

/**
 * Returns the region of the current user
 * @returns {Promise< string >} The region of the current user
 */
export const getRegion = async () => {
  const host = window.location.hostname

  if (host.includes('dayforcehcm.com') || host.includes('dayforce.com')) {
    try {
      const res = await axios.get(`${AUTH_URL}/users/region`, {
        headers: { ...(await getAuthHeader()) }
      })
      return res.data.region || 'eastus2'
    } catch (_e) {
      return 'eastus2'
    }
  }

  if (host.includes('hcm.aljazeera.net')) {
    return 'ajz'
  }

  if (host.includes('internal-app.agentnoon.com')) {
    return 'internal'
  }

  try {
    const user_email = firebase.auth().currentUser?.email
    // If Agentnoon user has explicitly selected a region, honor it
    if (user_email?.endsWith('@agentnoon.com')) {
      const override = getPersistedRegion()
      const endpoints = await getAllowedEndpoints()
      if (endpoints.has(override)) return override
      deletePersistedRegion()
    }
    if (host.includes('dayforce-prototype-2.agentnoon.com')) {
      return 'dayforce-prototype-2'
    }
    if (host.includes('dayforce-prototype.agentnoon.com')) {
      return 'dayforce-prototype'
    }
    // handle case for agentnoon emails
    if (user_email?.endsWith('@agentnoon.com')) {
      const address = user_email.split('@')[0]
      if (address.includes('+')) {
        // email+region@agentnoon.com
        return address.split('+')[1]
      } else if (address.includes('_')) {
        // test_service_region@agentnoon.com
        return address.split('_')[2]
      }
    }
    if (host.includes('dayforce-prototype.agentnoon.com')) {
      return 'dayforce-prototype'
    }

    const res = await axios.get(`${AUTH_URL}/users/region`, {
      headers: { ...(await getAuthHeader()) }
    })
    return res.data.region
  } catch (_e) {
    return 'us'
  }
}

let STAGING_API_URL = ''

export const getPreviewUrl = () => {
  logger.info('RUNNING DEV MODE API points to staging API')
  // This app is running on a preview
  // This is where the backend API is hosted on.
  if (STAGING_API_URL) {
    logger.info('Using memoized staging API URL', STAGING_API_URL)
    return STAGING_API_URL
  }

  const REGEX = /--pr\d+/
  const PR_NUMBER = window.location.hostname.match(REGEX)[0].split('--')[1].slice(2)
  logger.info('PR_NUMBER', PR_NUMBER)

  STAGING_API_URL = `https://${PR_NUMBER}.pr-api.agentnoon.co/v1`
  logger.info('Routing backend to', STAGING_API_URL)
  return STAGING_API_URL
}

/**
 * The API URL. It's cached to avoid unnecessary network requests.
 * @type {string|null}
 */
let _API_URL
/**
 * Returns the API URL. It caches the value and returns it if it's already set. If update is true, it will force a refresh of the API URL.
 * @param {boolean} update - Whether to force a refresh of the API URL
 * @returns {Promise<string>} The API URL
 */
export const API_URL = async (update = false) => {
  if (_API_URL && !update) return _API_URL

  if (window.location.hostname.includes('localhost') && import.meta.env.MODE != 'production') {
    const override = getPersistedRegion?.()
    if (override === 'staging') {
      _API_URL = STAGING_API
      return _API_URL
    }
    logger.info('RUNNING DEV MODE API points to localhost')
    _API_URL = LOCAL_HOST
    return _API_URL
  }

  if (window.location.hostname.includes('.web.app')) {
    const override = getPersistedRegion?.()
    if (override === 'staging') {
      _API_URL = STAGING_API
      return _API_URL
    }
    return getPreviewUrl()
  }

  if (window.location.hostname.includes('staging')) {
    _API_URL = STAGING_API
    return _API_URL
  }

  const region = await getRegion()
  logger.info('Using ', region, ' region')

  if (
    window.location.hostname.includes('dayforcehcm.com') ||
    window.location.hostname.includes('dayforce.com')
  ) {
    if (
      window.location.hostname.includes('np.dayforce') ||
      window.location.hostname.includes('np.mydayforce')
    ) {
      _API_URL = DAYFORCE_NP_API
    } else if (
      window.location.hostname.includes('pre.dayforce') ||
      window.location.hostname.includes('pre.mydayforce')
    ) {
      _API_URL = DAYFORCE_PRE_API
    } else {
      const DAYFORCE_REGION_TO_PROD_API = {
        eastus2: DAYFORCE_PROD_API,
        australiaeast: DAYFORCE_AU_API,
        canadacentral: DAYFORCE_CA_API,
        westeurope: DAYFORCE_EU_API
      }
      _API_URL = DAYFORCE_REGION_TO_PROD_API[region] || DAYFORCE_PROD_API
    }
    return _API_URL
  }

  // This is production if it made this far
  switch (region) {
    case 'dayforce-prototype':
      _API_URL = DAYFORCE_PROTOTYPE_API
      break
    case 'dayforce-prototype-2':
      _API_URL = DAYFORCE_PROTOTYPE_2_API
      break
    case 'eu':
      _API_URL = EU_PRODUCTION_API_V2
      break
    case 'aus':
      _API_URL = AUS_PRODUCTION_API
      break
    case 'singapore':
      _API_URL = SINGAPORE_PRODUCTION_API
      break
    case 'ajz':
      _API_URL = AJZ_PRODUCTION_API
      break
    case 'uae':
      _API_URL = UAE_PRODUCTION_API
      break
    case 'internal':
      _API_URL = INTERNAL_PRODUCTION_API
      break
    case 'localhost':
      _API_URL = LOCAL_HOST
      break
    default:
      _API_URL = PRODUCTION_API
      break
  }
  return _API_URL
}

const getAuthUrl = () => {
  if (window.location.hostname.includes('dayforce-prototype.agentnoon.com')) {
    return DAYFORCE_PROTOTYPE_API
  }
  if (window.location.hostname.includes('dayforce-prototype-2.agentnoon.com')) {
    return DAYFORCE_PROTOTYPE_2_API
  }
  if (window.location.hostname.includes('localhost') && import.meta.env.MODE != 'production') {
    return LOCAL_HOST
  }

  if (
    window.location.hostname.includes('dayforcehcm.com') ||
    window.location.hostname.includes('dayforce.com')
  ) {
    if (
      window.location.hostname.includes('np.dayforce') ||
      window.location.hostname.includes('np.mydayforce')
    )
      return DAYFORCE_NP_API
    if (
      window.location.hostname.includes('pre.dayforce') ||
      window.location.hostname.includes('pre.mydayforce')
    )
      return DAYFORCE_PRE_API
    return DAYFORCE_PROD_API
  }

  if (window.location.hostname.includes('.web.app')) {
    return getPreviewUrl()
  }

  if (window.location.hostname.includes('staging')) {
    return STAGING_API
  }

  // Exception for self-hosted instances
  if (window.location.hostname.includes('aljazeera.net')) {
    return AJZ_PRODUCTION_API
  }

  // For production, use the US server for auth calls
  return PRODUCTION_API
}

export const AUTH_URL = getAuthUrl()

export const getAuthHeader = async (forceRefresh = false) => {
  const token = await firebase.auth().currentUser?.getIdToken(forceRefresh)
  return { authorization: `Bearer ${token}` }
}

export const isAgentnoonReachable = async () => {
  try {
    logger.info('Checking reachability for:', `${await API_URL()}/ping`)
    const response = await fetch(`${await API_URL()}/ping`, {
      method: 'GET',
      cache: 'no-store'
    })
    logger.info('Ping response:', response.status)
    return response.ok
  } catch (error) {
    logger.error('Reachability check failed:', error.message)
    return false
  }
}

/**
 * Checks if the error is a network error and if the agentnoon application is blocked on the network
 * @param {Error} error - The error to check
 * @returns {Promise<boolean>} - Whether the agentnoon application is reachable on the network
 */
export const isFirewallError = async (error) => {
  const isNetworkError = error.message.includes('Network Error') || !error.response

  if (isNetworkError) {
    const reachable = await isAgentnoonReachable()
    if (!reachable && !isFirewallToastShowing.value) {
      logger.warn('Agentnoon is not reachable due to firewall/VPN')
      isFirewallToastShowing.value = true

      // Log to Sentry
      Sentry.captureMessage('Firewall/Network Access Issue', {
        level: 'warning',
        tags: {
          type: 'firewall_block',
          api_url: await API_URL(),
          region: await getRegion()
        },
        extra: {
          error: error.message,
          originalUrl: error.config?.url,
          timestamp: new Date().toISOString()
        }
      })

      queueToast(
        'Network error. Agentnoon application seems to be blocked on your network. Please contact your IT administrator.',
        {
          timeout: 25000,
          position: 'top',
          bg: 'bg-error-50',
          onClose: () => {
            isFirewallToastShowing.value = false
          }
        }
      )
      return reachable
    }
  }
  return true
}

export const setAPIUrl = async () => {
  await API_URL(true)
}
